/**
 * Chest Service
 * Manages reward chest generation and claiming (level-triggered and streak-triggered)
 */

import { supabase } from '@/lib/supabase';
import { isObtainable } from '@/lib/shopWindow';
import type { Chest, ChestTriggerType, ChestRewardType } from '@/models/Chest';
import { coinService } from './coin.service';
import { useAnalytics } from '@/composables/useAnalytics';

const analytics = useAnalytics();

export const chestService = {
  /**
   * Draw a reward from the chest_rewards_catalog using weighted random selection.
   * Falls back to coins if catalog is empty or unavailable.
   */
  async _drawRewardFromCatalog(): Promise<{ rewardType: ChestRewardType; rewardValue: string }> {
    const { data: catalog } = await supabase
      .from('chest_rewards_catalog' as any)
      .select('*')
      .eq('is_active', true)

    const items = (catalog ?? []) as any[]

    if (!items.length) {
      // Fallback: same as old behavior
      return { rewardType: 'coins', rewardValue: JSON.stringify({ amount: 10 }) }
    }

    const totalWeight = items.reduce((sum: number, item: any) => sum + item.weight, 0)
    let random = Math.random() * totalWeight

    for (const item of items) {
      random -= item.weight
      if (random <= 0) {
        if (item.reward_type === 'avatar') {
          // Une pose ou un décor, jamais hors de sa fenêtre d'acquisition : un
          // avatar Halloween ne sort pas d'un coffre en septembre. Même règle
          // que le tirage serveur (draw_chest_reward). Le filtre se fait ici et
          // non dans la requête : `select('*')` survit à une base qui n'a pas
          // encore les colonnes de fenêtre, un filtre SQL dessus, non.
          const { data: rows } = await supabase
            .from('shop_items' as any)
            .select('*')
            .in('type', ['pose', 'background'])
            .eq('is_active', true)
            .limit(100)
          const shopItems = ((rows ?? []) as any[]).filter(item => isObtainable(item))

          if (shopItems.length > 0) {
            const randomIndex = Math.floor(Math.random() * shopItems.length)
            const selectedItem = shopItems[randomIndex] as { id: string }
            return { rewardType: 'avatar', rewardValue: JSON.stringify({ shop_item_id: selectedItem.id }) }
          }
          // Fallback to coins if no shop items
          return { rewardType: 'coins', rewardValue: JSON.stringify({ amount: 10 }) }
        }
        return {
          rewardType: item.reward_type,
          rewardValue: JSON.stringify(
            item.reward_type === 'coins' ? { amount: item.reward_value } :
            item.reward_ref ? { shop_item_id: item.reward_ref } :
            { amount: item.reward_value }
          )
        }
      }
    }

    return { rewardType: 'coins', rewardValue: JSON.stringify({ amount: 10 }) }
  },

  /**
   * Generate a reward chest for a child
   * Called when level threshold reached or streak milestone hit.
   * @param childId UUID of the child
   * @param triggerType Type of trigger ('level' or 'streak')
   * @param triggerValue Value of trigger (e.g., level number or streak day)
   * @param householdId Optional household UUID (looked up if not provided)
   * @param streakDay Coffres de série : date locale (YYYY-MM-DD) du jour
   *   qualifié qui vaut ce coffre. C'est la clé du palier, la seule qui survive
   *   à un recalcul de la série (cf. chestMilestoneDays).
   * @returns Generated chest record
   */
  async generateChest(
    childId: string,
    triggerType: ChestTriggerType,
    triggerValue: number,
    householdId?: string,
    streakDay?: string
  ): Promise<Chest> {
    // If no householdId provided, look it up (backward compat for existing callers)
    if (!householdId) {
      const { data: child } = await supabase
        .from('children' as any)
        .select('household_id')
        .eq('id', childId)
        .single()
      householdId = (child as any)?.household_id
    }

    // For 'level' chests, check deduplication. Streak chests can repeat trigger values.
    if (triggerType === 'level') {
      const { data: existingChest, error: fetchError } = await supabase
        .from('chests' as any)
        .select('*')
        .eq('child_id', childId)
        .eq('trigger_type', triggerType)
        .eq('trigger_value', triggerValue)
        .maybeSingle();

      if (existingChest && !fetchError) {
        const chestData = existingChest as unknown as Chest;
        console.log('🎁 [chestService] Chest already exists, returning existing chest:', {
          id: chestData.id,
          childId,
          triggerType,
          triggerValue
        });
        return chestData;
      }
    }

    console.log('🆕 [chestService] Creating new chest:', {
      childId,
      triggerType,
      triggerValue
    });

    // Draw reward from catalog
    const { rewardType, rewardValue } = await this._drawRewardFromCatalog();

    // Create chest record
    const { data: chest, error } = await supabase
      .from('chests' as any)
      .insert({
        child_id: childId,
        household_id: householdId,
        trigger_type: triggerType,
        trigger_value: triggerValue,
        reward_type: rewardType,
        reward_value: rewardValue,
        is_claimed: false,
        ...(streakDay ? { streak_day: streakDay } : {})
      })
      .select()
      .single();

    if (error) {
      // If duplicate key error, fetch the existing chest
      if (error.code === '23505' || error.message.includes('duplicate key') || error.message.includes('unique constraint')) {
        console.log('🔄 [chestService] Duplicate chest detected, fetching existing chest');
        // Un coffre de série se retrouve par sa date de palier : deux séries
        // peuvent porter le même trigger_value, jamais le même jour qualifié.
        const query = supabase
          .from('chests' as any)
          .select('*')
          .eq('child_id', childId)
          .eq('trigger_type', triggerType);
        const { data: existingChest } = await (streakDay
          ? query.eq('streak_day', streakDay)
          : query.eq('trigger_value', triggerValue)
        ).maybeSingle();

        if (existingChest) {
          return existingChest as unknown as Chest;
        }
      }
      throw new Error(`Generation failed: ${error.message}`);
    }

    // Le stock durable apprend le coffre tout de suite, sans attendre l'aller-
    // retour Realtime de la notification : la pilule apparaît dans le même
    // geste que la validation qui a fait monter le niveau.
    //
    // Jamais attendu : le store importe ce service, donc l'import inverse doit
    // rester dynamique, et un import qui traîne ne doit pas retarder (ni bloquer)
    // la génération elle-même.
    void import('@/stores/chests')
      .then(({ useChestsStore }) => useChestsStore().noteGenerated(chest as unknown as Chest))
      .catch((storeError) => {
        // Hors contexte Pinia (tests, worker) — sans conséquence : le stock sera
        // rechargé depuis la table au prochain passage.
        console.warn('[chestService] Chest store not reachable (non-critical):', storeError);
      });

    // Track chest generation in analytics
    try {
      const chestData = chest as unknown as Chest;
      await analytics.logChestGenerated(
        childId,
        chestData.id,
        triggerType,
        triggerValue,
        rewardType
      );
    } catch (analyticsError) {
      // Don't fail chest generation if analytics fails
      console.warn('[chestService] Analytics tracking failed (non-critical):', analyticsError);
    }

    return chest as unknown as Chest;
  },

  /**
   * Claim/open a chest (child opens the chest)
   * @param childId UUID of the child
   * @param chestId UUID of the chest
   * @returns Updated chest record with reward details
   */
  async claimChest(childId: string, chestId: string): Promise<Chest> {
    // Get the chest
    const { data: chest, error: fetchError } = await supabase
      .from('chests' as any)
      .select('*')
      .eq('id', chestId)
      .eq('child_id', childId)
      .single();

    const chestData = chest as unknown as Chest;

    if (fetchError || !chestData) {
      throw new Error('Chest not found');
    }

    if (chestData.is_claimed) {
      throw new Error('Chest already claimed');
    }

    // Parse reward value
    let rewardData: { shop_item_id?: string; amount?: number };
    try {
      rewardData = JSON.parse(chestData.reward_value);
    } catch (error) {
      throw new Error('Invalid reward data');
    }

    // Award the reward based on type
    if (chestData.reward_type === 'coins' && rewardData.amount) {
      // Award coins (multiple times for the amount)
      for (let i = 0; i < rewardData.amount; i++) {
        await coinService.awardCoin(
          childId,
          'level_chest',
          chestId
        );
      }
    } else if (chestData.reward_type === 'avatar' && rewardData.shop_item_id) {
      // For avatar rewards, we need to create a purchase record if it doesn't exist
      // Check if already purchased
      const { data: existingPurchase } = await supabase
        .from('child_shop_purchases' as any)
        .select('id')
        .eq('child_id', childId)
        .eq('shop_item_id', rewardData.shop_item_id)
        .single();

      if (!existingPurchase) {
        // Create purchase record for the shop item (free, from chest)
        // Mark as gifted since it came from a Chest reward
        await supabase
          .from('child_shop_purchases' as any)
          .insert({
            child_id: childId,
            shop_item_id: rewardData.shop_item_id,
            coins_spent: 0, // Free from chest
            is_gifted: true, // Mark as gifted from Chest
            is_equipped: false
          });
      }
    }

    console.log('🔄 [chestService] Attempting to mark chest as claimed:', { chestId, childId });

    // Mark chest as claimed
    const { data: updatedChest, error: updateError } = await supabase
      .from('chests' as any)
      .update({
        is_claimed: true,
        claimed_at: new Date().toISOString()
      })
      .eq('id', chestId)
      .eq('child_id', childId)
      .select()
      .maybeSingle();

    if (updateError) {
      console.error('❌ [chestService] Update error:', updateError);
      throw new Error(`Claim failed: ${updateError.message}`);
    }

    if (!updatedChest) {
      console.error('❌ [chestService] Update failed to return data. Check RLS policies for UPDATE on "chests" table.');
      throw new Error('Claim persistence failed: Permission denied or row not found.');
    }

    console.log('✅ [chestService] Update successful, confirmed row:', (updatedChest as unknown as Chest).id);

    const resultChest = updatedChest as unknown as Chest;

    // Track chest opened in analytics
    try {
      const rewardValueForAnalytics = resultChest.reward_type === 'coins'
        ? (rewardData.amount || 0)
        : (rewardData.shop_item_id || '');

      await analytics.logChestOpened(
        childId,
        chestId,
        resultChest.reward_type,
        rewardValueForAnalytics
      );
    } catch (analyticsError) {
      // Don't fail chest claim if analytics fails
      console.warn('[chestService] Analytics tracking failed (non-critical):', analyticsError);
    }

    return resultChest;
  },

  /**
   * Parmi les jours de palier passés en argument, ceux qui ont déjà leur coffre.
   *
   * Remplace un comptage « coffres posés depuis le début de la série », qui
   * datait un coffre à son heure d'écriture au lieu du palier qu'il paie : un
   * coffre versé pour une lecture antérieure de la série absorbait le palier de
   * la lecture courante (cf. chestMilestoneDays).
   * @param childId UUID de l'enfant
   * @param days Dates locales (YYYY-MM-DD) des paliers à vérifier
   */
  async getPaidStreakDays(childId: string, days: string[]): Promise<Set<string>> {
    if (days.length === 0) return new Set();

    const { data, error } = await supabase
      .from('chests' as any)
      .select('streak_day')
      .eq('child_id', childId)
      .eq('trigger_type', 'streak')
      .in('streak_day', days);

    if (error) {
      // Remonté à l'appelant : mieux vaut ne pas générer que d'en générer en
      // double sur une lecture qui a échoué.
      throw new Error(`Failed to read paid streak days: ${error.message}`);
    }

    return new Set((data ?? []).map((row: any) => row.streak_day as string));
  },

  /**
   * COMBIEN de coffres de série portent une date comprise dans la fenêtre
   * donnée, sans regarder LESQUELLES.
   *
   * C'est la question qui rend le registre stable. Un palier est « le 3e, 6e,
   * 9e jour qualifié de la série », donc sa DATE bouge dès que la composition
   * de la série change — un jour d'absence déclaré après coup suffit. Demander
   * « ce jour précis a-t-il son coffre ? » répond alors non pour tous les
   * paliers d'un coup, et le rattrapage les repaie tous (incident du
   * 2026-09-03 : cinq coffres en une demi-seconde, dont trois rangs déjà
   * payés).
   *
   * Un COMPTE dans une fenêtre, lui, ne bouge pas quand les rangs se décalent.
   * Et il reste immunisé contre la panne inverse du 2026-08-17, où un total
   * fondé sur `generated_at` laissait un coffre de la série précédente absorber
   * un palier de la nouvelle : ici la fenêtre porte sur `streak_day`, la date
   * du palier PAYÉ, pas l'heure d'écriture.
   *
   * @param from premier jour de la série en cours (inclus)
   * @param to   dernier jour de la série en cours (inclus)
   */
  async countStreakChestsInWindow(childId: string, from: string, to: string): Promise<number> {
    const { count, error } = await supabase
      .from('chests' as any)
      .select('id', { count: 'exact', head: true })
      .eq('child_id', childId)
      .eq('trigger_type', 'streak')
      .gte('streak_day', from)
      .lte('streak_day', to);

    if (error) {
      // Même doctrine que getPaidStreakDays : sur une lecture en échec on
      // remonte, pour ne jamais générer en double sur une information absente.
      throw new Error(`Failed to count streak chests in window: ${error.message}`);
    }

    return count ?? 0;
  },

  /**
   * Coffres non réclamés de plusieurs enfants, en une requête (hydratation du
   * foyer au boot).
   * @param childIds UUIDs des enfants
   * @returns Map childId -> coffres non réclamés, du plus ancien au plus récent
   */
  async getUnclaimedChestsForChildren(childIds: string[]): Promise<Map<string, Chest[]>> {
    const result = new Map<string, Chest[]>();
    if (!childIds.length) return result;

    const { data, error } = await supabase
      .from('chests' as any)
      .select('*')
      .in('child_id', childIds)
      .eq('is_claimed', false)
      .order('generated_at', { ascending: true });

    if (error) {
      console.error('Error getting unclaimed chests for children:', error);
      throw new Error(`Failed to get unclaimed chests: ${error.message}`);
    }

    for (const chest of (data ?? []) as unknown as Chest[]) {
      const list = result.get(chest.child_id);
      if (list) list.push(chest);
      else result.set(chest.child_id, [chest]);
    }

    return result;
  },

  /**
   * Get list of unclaimed chests for a child
   * @param childId UUID of the child
   * @returns Array of unclaimed chests
   */
  async getUnclaimedChests(childId: string): Promise<Chest[]> {
    const { data, error } = await supabase
      .from('chests' as any)
      .select('*')
      .eq('child_id', childId)
      .eq('is_claimed', false)
      .order('generated_at', { ascending: false });

    if (error) {
      console.error('Error getting unclaimed chests:', error);
      throw new Error(`Failed to get unclaimed chests: ${error.message}`);
    }

    return (data || []) as unknown as Chest[];
  }
};
